import React, { useEffect } from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminDataProvider from "@/components/admin/AdminDataProvider";
import DashboardTab from "@/components/admin/tabs/DashboardTab";
import ProvidersTab from "@/components/admin/tabs/ProvidersTab";
import LeadAccountsTab from "@/components/admin/tabs/LeadAccountsTab";
import StrategiesTab from "@/components/admin/tabs/StrategiesTab";
import AccountsTab from "@/components/admin/tabs/AccountsTab";
import UsersTab from "@/components/admin/tabs/UsersTab";
import ArticlesTab from "@/components/admin/tabs/ArticlesTab";
import AuthorsTab from "@/components/admin/tabs/AuthorsTab";
import FaqsTab from "@/components/admin/tabs/FaqsTab";
import { useAdmin } from "@/contexts/admin/AdminContext";
import { useLocation, useNavigate } from "react-router-dom";

const AdminContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { 
    activeTab, 
    setActiveTab,
    users,
    strategies,
    providers,
    accountPerformances,
    accounts,
    stripeSubscriptions,
    leadAccounts,
    getUsersForProviders,
    updateUserRole,
    updateUserName,
    deleteUser,
    toggleStrategyStatus,
    createStrategy,
    updateStrategy,
    deleteStrategy,
    toggleStrategyActive,
    approveStrategy,
    createProvider,
    updateProvider,
    deleteProvider,
    toggleProviderActive,
    assignUserToProvider,
    removeUserFromProvider,
    createLeadAccount,
    updateLeadAccount,
    deleteLeadAccount,
    createAccount,
    updateAccount,
    deleteAccount,
    toggleAccountActive,
    sortField,
    sortOrder,
    handleSort,
    userRole,
    
    articles,
    articleTags,
    articleAuthors,
    createArticle,
    updateArticle,
    deleteArticle,
    createArticleTag,
    uploadArticleMedia,
    
    createAuthor,
    updateAuthor,
    deleteAuthor,
    
    faqs,
    createFaq,
    updateFaq,
    deleteFaq
  } = useAdmin();
  
  const isAdmin = userRole === 'admin';
  const isEditor = userRole === 'editor';
  const canManageContent = isAdmin || isEditor;
  
  useEffect(() => {
    if (location.state?.activeTab && location.state.activeTab !== activeTab) {
      console.log("Setting active tab from location state:", location.state.activeTab);
      setActiveTab(location.state.activeTab);
    }
    
    if (!location.state?.activeTab && location.key) {
      console.log("Direct navigation detected, active tab:", activeTab);
    }
  }, [location, activeTab, setActiveTab]);
  
  useEffect(() => {
    if (isEditor && activeTab === 'dashboard') {
      console.log("Editor redirected to articles tab");
      setActiveTab('articles');
    }
  }, [isEditor, activeTab, setActiveTab]);
  
  const handleTabChange = (newTab: string) => {
    console.log("Tab changed to:", newTab);
    setActiveTab(newTab);
    navigate('/admin', { 
      state: { activeTab: newTab },
      replace: true
    });
  };
  
  return (
    <AdminLayout 
      activeTab={activeTab} 
      setActiveTab={handleTabChange} 
      userRole={userRole || ''}
    >
      {!isEditor && (
        <DashboardTab 
          users={users}
          strategies={strategies}
          providers={providers}
          accountPerformances={accountPerformances}
          accounts={accounts}
          leadAccounts={leadAccounts}
          stripeSubscriptions={stripeSubscriptions}
          userRole={userRole}
        />
      )}
      
      {isAdmin && (
        <ProvidersTab 
          providers={providers}
          users={getUsersForProviders()}
          createProvider={createProvider}
          updateProvider={updateProvider}
          deleteProvider={deleteProvider}
          toggleProviderActive={toggleProviderActive}
          assignUserToProvider={assignUserToProvider}
          removeUserFromProvider={removeUserFromProvider}
        />
      )}

      {!isEditor && (
        <>
          <LeadAccountsTab 
            leadAccounts={leadAccounts}
            providers={providers}
            createLeadAccount={createLeadAccount}
            updateLeadAccount={updateLeadAccount}
            deleteLeadAccount={deleteLeadAccount}
            userRole={userRole}
          />
          
          <StrategiesTab 
            strategies={strategies}
            providers={providers}
            toggleStrategyStatus={toggleStrategyStatus}
            createStrategy={createStrategy}
            updateStrategy={updateStrategy}
            deleteStrategy={deleteStrategy}
            toggleStrategyActive={toggleStrategyActive}
            approveStrategy={approveStrategy}
            userRole={userRole}
          />
          
          <AccountsTab 
            accounts={accounts}
            leadAccounts={leadAccounts}
            strategies={strategies}
            createAccount={createAccount}
            updateAccount={updateAccount}
            deleteAccount={deleteAccount}
            toggleAccountActive={toggleAccountActive}
            userRole={userRole}
          />
        </>
      )}
      
      {isAdmin && (
        <UsersTab 
          users={users}
          strategies={strategies}
          providers={providers}
          updateUserRole={updateUserRole}
          updateUserName={updateUserName}
          deleteUser={deleteUser}
          sortField={sortField}
          sortOrder={sortOrder}
          handleSort={handleSort}
        />
      )}
      
      {canManageContent && (
        <>
          <ArticlesTab 
            articles={articles}
            tags={articleTags}
            authors={articleAuthors}
            createArticle={createArticle}
            updateArticle={updateArticle}
            deleteArticle={deleteArticle}
            createTag={createArticleTag}
            uploadMedia={uploadArticleMedia}
          />
          
          <AuthorsTab 
            authors={articleAuthors || []}
            createAuthor={createAuthor}
            updateAuthor={updateAuthor}
            deleteAuthor={deleteAuthor}
            uploadMedia={uploadArticleMedia}
          />
          
          <FaqsTab 
            faqs={faqs || []}
            createFaq={createFaq}
            updateFaq={updateFaq}
            deleteFaq={deleteFaq}
            uploadMedia={uploadArticleMedia}
          />
        </>
      )}
    </AdminLayout>
  );
};

const Admin = () => {
  return (
    <AdminDataProvider>
      <AdminContent />
    </AdminDataProvider>
  );
};

export default Admin;
